'use client'
import Tag from '@/components/Atoms/Tag'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useSuspenseQuery } from '@apollo/experimental-nextjs-app-support/ssr'
import { gql } from '@apollo/client'
import { PostNewsProps } from '@/types'
import parse from 'html-react-parser'
import Link from 'next/link'
import { format } from 'date-fns'
import { useWindowSize } from '@/hooks/useWindowSize'

export default function DetailContentSession(props: { slug: string }) {
  const [isOpen, setOpen] = useState<boolean>(false)
  const [isShow, setIsShow] = useState<boolean>(false)
  const [infoLength, setInfoLength] = useState<number>(0)
  const [childHeight, setChildHeight] = useState<number>(0)
  const { width } = useWindowSize()
  const listInfoRef = useRef<HTMLDListElement>(null)
  useEffect(() => {
    if (listInfoRef.current) {
      const childCount = listInfoRef.current.childElementCount
      setInfoLength(childCount)

      // Get all child elements
      const children = listInfoRef.current.children
      // Calculate the total height of the first 3 child elements
      const totalHeight = Array.from(children)
        .slice(0, 3)
        .reduce((acc, child) => {
          const childElement = child as HTMLElement
          return acc + childElement.offsetHeight
        }, 0)
      setChildHeight(totalHeight + 62)
    }
  }, [width])

  const query = gql`
    query GetInformation {
      getInformation(slug:"${props.slug}") {
        id
        title
        createdAt
        publishAt
        content
        imageFileName
        slug
        categories {
          id
          name
          slug
        }
        informationTags {
          id
          name
          slug
        }
        informationMeta {
          id
          name
          title
        }
        informationInformationMeta {
          informationMetumId
          value
        }
      }
    }
  `

  const { data } = useSuspenseQuery<{
    getInformation: PostNewsProps
  }>(query)

  const information = data.getInformation

  const filterData = (data: PostNewsProps, order: string) => {
    const selectedMetas = data.informationMeta?.filter((i) => i.name === order)
    if (selectedMetas.length) {
      const targetMeta = data.informationInformationMeta?.filter(
        (i) => i.informationMetumId === Number(selectedMetas[0].id)
      )
      return targetMeta ? targetMeta[0].value : 'test'
    } else {
      return 'null'
    }
  }

  const variants = {
    hidden: {
      height: 0,
      opacity: 0,
    },
    visible: {
      height: 'auto',
      opacity: 1,
    },
  }

  return (
    <div className="relative">
      <div className="max-w-[736px] mx-auto">
        <Image
          src={
            process.env.NEXT_PUBLIC_AWS_S3_URL +
            '/' +
            information.id +
            '/original_' +
            information.imageFileName
          }
          alt="img"
          width={2000}
          height={700}
          className={'w-full h-auto md:rounded-md'}
          priority
        />
        <div className="px-6 md:px-0">
          <div>
            <h2
              className="font-bold text-[22px] md:text-[28px] leading-[1.7] tracking-[0.02em] text-center mt-6 md:mt-8"
              dangerouslySetInnerHTML={{ __html: information.title }}
            />
            <div className="flex items-center flex-wrap justify-center mt-2">
              <span className="text-center text-[12px] leading-[2] inline-block text-darkgrey_200 font-bold w-full md:w-auto">
                {format(information.publishAt, 'yy.MM.dd')}
              </span>
              <div className="flex items-center flex-wrap text-center text-[11px] md:text-[12px] leading-[2] font-medium md:border-l md:border-l-darkgrey_100 md:ml-4 md:pl-4">
                {information.categories.map((item, index) => (
                  <>
                    {item.name !== 'ピックアップ' &&
                      item.name !== 'リクルートサイト' && (
                        <Link
                          key={index}
                          href={'/interview/category/' + item.slug}
                          className="text-[12px] inline-block text-khaki_400 bg-black_50 rounded-[2px] px-3 py-[2px] hover:text-blue_400 transition-colors duration-300"
                        >
                          {item.name}
                        </Link>
                      )}
                  </>
                ))}
                {information.informationTags.map((item, index) => (
                  <Tag
                    key={item.name + index}
                    href={''}
                    type="text"
                    name={'#' + item.name}
                  />
                ))}
              </div>
            </div>
            <hr className="border-dashed mt-10 mb-8 md:mt-14 md:mb-10" />
          </div>
          <div className="relative"></div>
          {`${filterData(information, 'recruit_menber_text_1')}` !== `null` &&
            `${filterData(information, 'recruit_menber_text_1')}` !== '' && (
              <div className="fixed bottom-6 left-0 z-[999] w-full max-w-[239px] hidden lg:block">
                <div
                  className={`w-full border bg-white border-darkgrey_100 rounded-r-lg py-5 pl-8 pr-4 overflow-hidden`}
                >
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setOpen(!isOpen)}
                  >
                    <p
                      className={`font-avenirNextBold text-sm tracking-[0.02em] uppercase ${isOpen ? 'text-blue_400' : 'text-darkgrey_500'}`}
                    >
                      MEMBER PROFILE &#x1f4ac;
                    </p>
                    <motion.div
                      initial={{ rotate: 0 }}
                      animate={{ rotate: isOpen ? 0 : 180 }}
                      transition={{ duration: 0.3 }}
                      className="ml-3 absolute right-4 top-[18px] leading-none text-blue_400"
                    >
                      <span className="material-symbols-outlined">
                        expand_more
                      </span>
                    </motion.div>
                  </div>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        variants={variants}
                        initial={'hidden'}
                        animate={'visible'}
                        exit={'hidden'}
                        transition={{
                          duration: 0.3,
                        }}
                      >
                        <div className="pt-5 [&>*:last-child]:!mb-0">
                          <div className="flex mb-3">
                            <Image
                              src={filterData(
                                information,
                                'recruit_menber_profile_1'
                              )}
                              alt=""
                              width={40}
                              height={40}
                              className="w-10 h-10 rounded-sm"
                            />
                            <div className="ml-3">
                              <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                {filterData(
                                  information,
                                  'recruit_menber_text_1'
                                )}
                              </p>
                              <p className="text-[10px] leading-[2] text-khaki_400">
                                {filterData(
                                  information,
                                  'recruit_menber_occupation_1'
                                )}
                              </p>
                            </div>
                          </div>
                          {`${filterData(information, 'recruit_menber_text_2')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_2')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_2'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_2'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_2'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          {`${filterData(information, 'recruit_menber_text_3')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_3')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_3'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_3'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_3'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          {`${filterData(information, 'recruit_menber_text_4')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_4')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_4'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_4'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_4'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          {`${filterData(information, 'recruit_menber_text_5')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_5')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_5'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_5'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_5'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          {`${filterData(information, 'recruit_menber_text_6')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_6')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_6'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_6'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_6'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          {`${filterData(information, 'recruit_menber_text_7')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_7')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_7'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_7'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_7'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          {`${filterData(information, 'recruit_menber_text_8')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_8')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_8'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_8'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_8'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                          {`${filterData(information, 'recruit_menber_text_9')}` !==
                            `null` &&
                            `${filterData(information, 'recruit_menber_text_9')}` !==
                              '' && (
                              <div className="flex mb-3">
                                <Image
                                  src={filterData(
                                    information,
                                    'recruit_menber_profile_9'
                                  )}
                                  alt=""
                                  width={40}
                                  height={40}
                                  className="w-10 h-10 rounded-sm"
                                />
                                <div className="ml-3">
                                  <p className="text-[12px] font-bold leading-[1.7] tracking-[0.02em]">
                                    {filterData(
                                      information,
                                      'recruit_menber_text_9'
                                    )}
                                  </p>
                                  <p className="text-[10px] leading-[2] text-khaki_400">
                                    {filterData(
                                      information,
                                      'recruit_menber_occupation_9'
                                    )}
                                  </p>
                                </div>
                              </div>
                            )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}
          <div className="detailContentStyle">
            <div>
              {`${filterData(information, 'recruit_menber_text_1')}` !==
                `null` &&
                `${filterData(information, 'recruit_menber_text_1')}` !==
                  '' && (
                  <div>
                    <p className="text-[14px] md:text-[16px] font-bold leading-[1.7] blue">
                      メンバー
                    </p>
                    <motion.div
                      className="mt-6 overflow-hidden relative"
                      initial={{
                        height:
                          infoLength > 3 && width < 769 ? childHeight : 'auto',
                      }}
                      animate={{
                        height: isShow
                          ? 'auto'
                          : infoLength > 3 && width < 769
                            ? childHeight
                            : 'auto',
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <dl className="flex flex-wrap !mt-0" ref={listInfoRef}>
                        <dt>
                          <Image
                            src={filterData(
                              information,
                              'recruit_menber_thumb_1'
                            )}
                            alt="img"
                            width={736}
                            height={368}
                          />
                          <p>
                            {filterData(information, 'recruit_menber_text_1')}
                            <span>
                              {filterData(
                                information,
                                'recruit_menber_occupation_1'
                              )}
                            </span>
                          </p>
                        </dt>
                        {`${filterData(information, 'recruit_menber_text_2')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_2')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_2'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_2'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_2'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                        {`${filterData(information, 'recruit_menber_text_3')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_3')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_3'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_3'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_3'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                        {`${filterData(information, 'recruit_menber_text_4')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_4')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_4'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_4'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_4'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                        {`${filterData(information, 'recruit_menber_text_5')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_5')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_5'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_5'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_5'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                        {`${filterData(information, 'recruit_menber_text_6')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_6')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_6'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_6'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_6'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                        {`${filterData(information, 'recruit_menber_text_7')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_7')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_7'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_7'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_7'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                        {`${filterData(information, 'recruit_menber_text_8')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_8')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_8'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_8'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_8'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                        {`${filterData(information, 'recruit_menber_text_9')}` !==
                          `null` &&
                          `${filterData(information, 'recruit_menber_text_9')}` !==
                            '' && (
                            <dt>
                              <Image
                                src={filterData(
                                  information,
                                  'recruit_menber_thumb_9'
                                )}
                                alt="img"
                                width={736}
                                height={368}
                              />
                              <p>
                                {filterData(
                                  information,
                                  'recruit_menber_text_9'
                                )}
                                <span>
                                  {filterData(
                                    information,
                                    'recruit_menber_occupation_9'
                                  )}
                                </span>
                              </p>
                            </dt>
                          )}
                      </dl>
                      {!isShow && infoLength > 3 && width < 769 && (
                        <div
                          className="absolute bottom-0 left-0 w-full h-[50px] md:hidden"
                          style={{
                            background:
                              'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.5) 68%, rgba(255,255,255,0) 95%)',
                          }}
                        />
                      )}
                    </motion.div>
                    {infoLength > 3 && width < 769 && (
                      <div
                        className="flex justify-center items-center md:hidden relative bg-white !mt-6 md:!mt-0 cursor-pointer"
                        onClick={() => {
                          setIsShow(!isShow)
                        }}
                      >
                        <span className="mr-2 text-[14px] leading-none font-bold tracking-[0.28px] !mt-0">
                          {!isShow ? 'もっと見る' : '閉じる'}
                        </span>
                        <motion.div
                          initial={{ rotate: 0 }}
                          animate={{ rotate: isShow ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                          className="leading-none text-blue_400 !mt-0"
                        >
                          <span className="material-symbols-outlined !mt-0">
                            expand_more
                          </span>
                        </motion.div>
                      </div>
                    )}
                    <hr className="dot !my-8 md:!mt-4" />
                  </div>
                )}
            </div>
            {parse(information.content)}
          </div>
        </div>
      </div>
    </div>
  )
}
